import { TeamDriverFull, TeamDriverRow, TeamListPage, TeamMemberRow } from '@numeo/types-main-web-app'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { ListTeamDriversArg, ListTeamMembersArg, teamApi, useGetScopedDriverRosterQuery } from '@/store/api/teamApi'
import {
    listDriversFromUsers,
    listMembersFromUsers,
    normalizeDriversQuery,
    normalizeMembersQuery,
    TEAM_PAGE_SIZE,
    TeamDriversQuery,
    TeamMembersQuery,
} from '@/utils/team/teamQuery'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

/** A picker's options: the pages read so far of one query, and a way to read the next. */
export interface TeamPagedList<T> {
    /** Every row of the pages read so far, in the server's order. */
    items: T[]
    /** How many rows the whole query matches. */
    total: number
    /** More rows match than have been read: `loadMore` reads the next page. */
    hasMore: boolean
    /** Read the next page (100 rows). Does nothing while one is in flight or when there is none. */
    loadMore: () => void
    /** The first page has not answered yet. */
    isLoading: boolean
    /** A page is in flight. */
    isFetching: boolean
    isError: boolean
}

type PageState<T> = { data?: TeamListPage<T>; isError: boolean; isFetching: boolean }
/** A cached query's state; a request not yet started carries none of the flags. */
type QueryResult<T> = { data?: TeamListPage<T>; isError?: boolean; isLoading?: boolean; isFetching?: boolean }

const pageStateOf = <T>(result: QueryResult<T>): PageState<T> => ({
    data: result.data,
    isError: !!result.isError,
    isFetching: !!result.isFetching || !!result.isLoading,
})
const samePages = <T>(a: PageState<T>[], b: PageState<T>[]): boolean =>
    a.length === b.length &&
    a.every((page, index) => page.data === b[index].data && page.isError === b[index].isError && page.isFetching === b[index].isFetching)

const NONE: never[] = []
const SKIPPED = { items: NONE, total: 0, hasMore: false, loadMore: () => undefined, isLoading: false, isFetching: false, isError: false }

/** How many pages of a query to hold: 1 again whenever the query (a new search, a filter) changes. */
const usePageCount = (key: string): [number, () => void] => {
    const [state, setState] = useState({ key, count: 1 })
    const count = state.key === key ? state.count : 1
    const more = useCallback(() => setState((previous) => ({ key, count: (previous.key === key ? previous.count : 1) + 1 })), [key])
    return [count, more]
}

/** Pages 1..count of `query` as request arguments, or none while nothing may be asked. */
const pageArgsOf = <Q extends object>(
    applicationId: string | undefined,
    active: boolean,
    key: string,
    count: number
): (Q & { applicationId: string; page: number })[] => {
    if (!active || !applicationId) return []
    const base = JSON.parse(key) as Q
    return Array.from({ length: count }, (_, index) => ({ ...base, applicationId, page: index + 1 }))
}

/** The answer across the pages read, with `loadMore` asking for the next only when there is one and none is in flight. */
const pagedListOf = <T>(pages: PageState<T>[], more: () => void): TeamPagedList<T> => {
    const items = pages.flatMap((page) => page.data?.items ?? NONE)
    const total = pages[0]?.data?.pagination.total ?? 0
    const isFetching = pages.some((page) => page.isFetching)
    const hasMore = !!pages[0]?.data && items.length < total
    return {
        items,
        total,
        hasMore,
        loadMore: () => {
            if (hasMore && !isFetching) more()
        },
        isLoading: !pages[0]?.data && !pages[0]?.isError,
        isFetching,
        isError: pages.some((page) => page.isError),
    }
}

/** Before the directory serves the company: the same pages from the roster the client already holds (or is reading). */
const localListOf = <T>(page: TeamListPage<T>, more: () => void, state: { isLoading?: boolean; isError?: boolean } = {}): TeamPagedList<T> => ({
    items: page.items,
    total: page.pagination.total,
    hasMore: page.items.length < page.pagination.total,
    loadMore: more,
    isLoading: !!state.isLoading,
    isFetching: !!state.isLoading,
    isError: !!state.isError,
})

/**
 * The company's drivers for a picker: a page of 100 at a time, searched and filtered on the server across every driver,
 * with `loadMore` for the next page (on scroll, or a "Load more" option). Never every driver at once.
 */
export const useTeamDriverOptions = (query: TeamDriversQuery = {}, options: { skip?: boolean; scoped?: boolean } = {}): TeamPagedList<TeamDriverRow> => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const skip = !!options.skip
    // Before the directory serves the company, `scoped` reads the Groups-scoped roster rather than `application.users`
    // (as `useTeamDrivers` does): the pickers that assign drivers offer only the ones the caller may work with.
    const scopedRoster = !enabled && !!options.scoped && !skip
    const roster = useGetScopedDriverRosterQuery(scopedRoster && applicationId ? { applicationId } : skipToken)
    const key = JSON.stringify(normalizeDriversQuery({ ...query, page: 1, limit: TEAM_PAGE_SIZE }))
    const [count, more] = usePageCount(key)
    const args = useMemo(() => pageArgsOf<ListTeamDriversArg>(applicationId, enabled && !skip, key, count), [applicationId, enabled, skip, key, count])

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.listTeamDrivers.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.listTeamDrivers.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => pageStateOf<TeamDriverRow>(select(state))), samePages)
    const users = useAppSelector((state) => (enabled || scopedRoster ? undefined : state.auth.application?.users))
    const source = scopedRoster ? roster.data : users

    return useMemo(() => {
        if (skip) return SKIPPED
        if (!enabled) {
            const page = listDriversFromUsers(source, { ...(JSON.parse(key) as TeamDriversQuery), limit: TEAM_PAGE_SIZE * count })
            return localListOf(page, more, scopedRoster ? { isLoading: roster.isLoading, isError: roster.isError } : {})
        }
        return pagedListOf(pages, more)
    }, [skip, enabled, source, scopedRoster, roster.isLoading, roster.isError, key, count, pages, more])
}

/** The same for members (admins, dispatchers, accountants), optionally of some roles. */
export const useTeamMemberOptions = (query: TeamMembersQuery = {}, options: { skip?: boolean } = {}): TeamPagedList<TeamMemberRow> => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const skip = !!options.skip
    const key = JSON.stringify(normalizeMembersQuery({ ...query, page: 1, limit: TEAM_PAGE_SIZE }))
    const [count, more] = usePageCount(key)
    const args = useMemo(() => pageArgsOf<ListTeamMembersArg>(applicationId, enabled && !skip, key, count), [applicationId, enabled, skip, key, count])

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.listTeamMembers.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.listTeamMembers.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => pageStateOf<TeamMemberRow>(select(state))), samePages)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))
    const ownerId = useAppSelector((state) => (state.auth.application?.owner ? String(state.auth.application.owner) : undefined))

    return useMemo(() => {
        if (skip) return SKIPPED
        if (!enabled)
            return localListOf(listMembersFromUsers(users, ownerId, { ...(JSON.parse(key) as TeamMembersQuery), limit: TEAM_PAGE_SIZE * count }), more)
        return pagedListOf(pages, more)
    }, [skip, enabled, users, ownerId, key, count, pages, more])
}

/**
 * Drivers' records (cards, role, discount; `view=records`, DRIVER_PROFILES:VIEW) for the importers' manual-assign picker:
 * a page of 100 at a time, searched on the server, with `loadMore`. Only while the directory serves the company. A 403
 * shows as `isError` on the first page with `isForbidden`.
 */
export const useTeamDriverRecordOptions = (
    search: string | undefined,
    options: { skip?: boolean } = {}
): TeamPagedList<TeamDriverFull> & { isForbidden: boolean } => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const term = normalizeDriversQuery({ search }).search
    const key = JSON.stringify({ search: term })
    const [count, more] = usePageCount(key)
    const args = useMemo(
        () => pageArgsOf<{ applicationId: string; page: number; search?: string }>(applicationId, enabled && !options.skip, key, count),
        [applicationId, enabled, options.skip, key, count]
    )

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.getTeamDriverRecords.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.getTeamDriverRecords.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => pageStateOf<TeamDriverFull>(select(state))), samePages)
    const forbidden = useAppSelector((state: RootState) => {
        const first = selectors[0]?.(state)
        return !!first?.error && typeof first.error === 'object' && 'status' in first.error && first.error.status === 403
    })

    return useMemo(
        () => ({ ...(options.skip || !enabled ? SKIPPED : pagedListOf(pages, more)), isForbidden: forbidden }),
        [options.skip, enabled, pages, more, forbidden]
    )
}
